import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Building2,
  Search,
  RefreshCcw,
  Download,
  Printer,
  Hash,
  Type,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com";

const EarningLeaveFormReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId = localStorage.getItem("branchId") || "";   // make sure you store this
  const branchType = localStorage.getItem("branchType") || "HO";    // make sure you store this

  // meta
  const [branches, setBranches] = useState([]);
  const [years, setYears] = useState([]);

  // filters
  const [branchId, setBranchId] = useState("Select");
  const [year, setYear] = useState("Select");
  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");

  // autocomplete
  const [empCodeSug, setEmpCodeSug] = useState([]);
  const [empNameSug, setEmpNameSug] = useState([]);
  const [showCodeSug, setShowCodeSug] = useState(false);
  const [showNameSug, setShowNameSug] = useState(false);

  // list
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-51", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const fetchMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(loginBranchId).trim() || !String(branchType).trim()) {
      toast.warn("Missing login branch / branchType in localStorage.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/earning-leave-form-report/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(loginBranchId),
          branchType,
        },
      });

      if (res?.data?.success) {
        setBranches(res.data.branches || []);
        setYears(res.data.years || []);

        setBranchId(String(res.data.defaultBranchId || "Select"));
        setYear(String(res.data.defaultYear || "Select"));
      } else {
        toast.error(res?.data?.message || "Meta load failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async (p = 1) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(loginBranchId).trim() || !String(branchType).trim()) {
      toast.warn("Missing login branch / branchType in localStorage.");
      return;
    }
    if (!year || year === "Select") {
      toast.warn("Please Select Year");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/earning-leave-form-report/data`, {
        params: {
          finYearId: Number(finYearId),
          branchId,
          branchType,
          loginBranchId,
          year,
          empCode: empCode.trim(),
          empName: empName.trim(),
          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
      } else {
        toast.error(res?.data?.message || "Failed to load report");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await fetchMeta();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // auto fetch after meta has set default year/branch
    if (year && year !== "Select" && branchId && branchId !== "Select") {
      fetchList(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, branchId]);

  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // --- Autocomplete (simple debounce)
  useEffect(() => {
    const t = setTimeout(async () => {
      const q = empCode.trim();
      if (!q) {
        setEmpCodeSug([]);
        return;
      }
      try {
        const res = await axios.get(`${BASE_URL}/api/earning-leave-form-report/autocomplete/empcode`, {
          params: { finYearId: Number(finYearId), branchId, branchType, q },
        });
        if (res?.data?.success) setEmpCodeSug(res.data.items || []);
      } catch {}
    }, 350);
    return () => clearTimeout(t);
  }, [empCode, finYearId, branchId, branchType]);

  useEffect(() => {
    const t = setTimeout(async () => {
      const q = empName.trim();
      if (!q) {
        setEmpNameSug([]);
        return;
      }
      try {
        const res = await axios.get(`${BASE_URL}/api/earning-leave-form-report/autocomplete/empname`, {
          params: { finYearId: Number(finYearId), branchId, branchType, q },
        });
        if (res?.data?.success) setEmpNameSug(res.data.items || []);
      } catch {}
    }, 350);
    return () => clearTimeout(t);
  }, [empName, finYearId, branchId, branchType]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handleRefresh = async () => {
    await fetchList(page);
  };

  // Full-page open (no popup)
  const openEmployeeOpeningLeave = (employeeId) => {
    if (!employeeId) return;
    const y = year && year !== "Select" ? year : new Date().getFullYear();
    // change this route to your React route when you build that page
    window.location.href = `/PayRoll/EmployeeOpeningLeave?code=${employeeId}&year=${y}`;
  };

const handlePrintFullPage = () => {
  const qs = new URLSearchParams({
    branchId: String(branchId),
    year: String(year),
    empCode: empCode.trim(),
    empName: empName.trim(),
  }).toString();

  const url = `/PayRoll/EarningLeaveFormPrint?${qs}`;

  // ✅ open in new tab
  window.open(url, "_blank", "noopener,noreferrer");
};


  // const handleExcel = async () => {
  //   // We will implement download later if you want as file stream.
  //   // For now we just open print-data in browser to verify data.
  //   const qs = new URLSearchParams({
  //     finYearId: String(finYearId),
  //     branchId: String(branchId),
  //     branchType: String(branchType),
  //     loginBranchId: String(loginBranchId),
  //     year: String(year),
  //     empCode: empCode.trim(),
  //     empName: empName.trim(),
  //   }).toString();

  //   window.open(`${BASE_URL}/api/earning-leave-form-report/print-data?${qs}`, "_blank");
  // };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EARNING LEAVE FORM REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Month-wise Leave Summary
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Search / Pagination / BL navigation (full page)
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleRefresh} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
{/* 
              <button onClick={handleExcel} className={ghostBtn} disabled={loading}>
                <Download className="w-4 h-4" />
                Export Data
              </button> */}

              <button onClick={handlePrintFullPage} className={primaryBtn} disabled={loading}>
                <Printer className="w-4 h-4" />
                Print
              </button>
            </div>
          </div>

          {/* Filters */}
          <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Employee Code */}
              <div className="relative">
                <label className={labelBase}>Employee Code</label>
                <div className="mt-2 flex items-center gap-2">
                  <Hash className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    value={empCode}
                    onChange={(e) => { setEmpCode(e.target.value); setShowCodeSug(true); }}
                    onBlur={() => setTimeout(() => setShowCodeSug(false), 150)}
                    placeholder="Type employee code..."
                    className={inputBase}
                  />
                </div>

                {showCodeSug && empCodeSug.length > 0 && (
                  <div className={`absolute z-30 mt-2 w-full rounded-2xl border overflow-hidden ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
                    {empCodeSug.slice(0, 10).map((x, i) => (
                      <button
                        type="button"
                        key={i}
                        onClick={() => { setEmpCode(x); setShowCodeSug(false); }}
                        className={`w-full text-left px-4 py-2 text-sm hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}
                      >
                        {x}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Year */}
              <div>
                <label className={labelBase}>
                  Year <span className="text-red-500">*</span>
                </label>
                <select value={year} onChange={(e) => setYear(e.target.value)} className={inputBase}>
                  <option value="Select">Select</option>
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              {/* Employee Name */}
              <div className="relative">
                <label className={labelBase}>Employee Name</label>
                <div className="mt-2 flex items-center gap-2">
                  <Type className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    value={empName}
                    onChange={(e) => { setEmpName(e.target.value); setShowNameSug(true); }}
                    onBlur={() => setTimeout(() => setShowNameSug(false), 150)}
                    placeholder="Type employee name..."
                    className={inputBase}
                  />
                </div>

                {showNameSug && empNameSug.length > 0 && (
                  <div className={`absolute z-30 mt-2 w-full rounded-2xl border overflow-hidden ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
                    {empNameSug.slice(0, 10).map((x, i) => (
                      <button
                        type="button"
                        key={i}
                        onClick={() => { setEmpName(x); setShowNameSug(false); }}
                        className={`w-full text-left px-4 py-2 text-sm hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}
                      >
                        {x}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Branch */}
              <div>
                <label className={labelBase}>Branch</label>
                <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className={inputBase}>
                  <option value="Select">Select</option>
                  {branches.map((b) => (
                    <option key={b.fld_branchid} value={String(b.fld_branchid)}>
                      {b.fld_branchname}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-5 flex justify-end">
              <button type="submit" className={primaryBtn} disabled={loading}>
                <Search className="w-4 h-4" />
                Search
              </button>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Emp Code</th>
                    <th className="px-4 py-4 text-center font-bold">Emp Name</th>
                    <th className="px-4 py-4 text-center font-bold">Designation</th>
                    <th className="px-4 py-4 text-center font-bold">OB</th>
                    {["Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar"].map((m) => (
                      <th key={m} className="px-4 py-4 text-center font-bold">{m}</th>
                    ))}
                    <th className="px-4 py-4 text-center font-bold">BL</th>
                    <th className="px-4 py-4 text-center font-bold">Lapsed Leave</th>
                    <th className="px-4 py-4 text-center font-bold">Location</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 1 + 4 + 1 + 12 + 3 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={1 + 4 + 1 + 12 + 3} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={(r.fld_employeeid ?? r.fld_Employeeid ?? idx) + "-" + idx} className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_employeecode ?? r.fld_Employeecode}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_employee ?? r.fld_Employee}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_designationname}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.OB}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.April}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.May}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.June}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.July}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.August}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.September}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.October}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.November}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.December}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.January}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.February}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.March}</td>

                        <td className="px-4 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => openEmployeeOpeningLeave(r._nav?.employeeId)}
                            className="font-extrabold underline text-blue-600 hover:text-blue-700"
                            title="Open Employee Opening Leave"
                          >
                            {r.Bal}
                          </button>
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.LapsedLeave}
                        </td>

                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_branchname}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`px-4 py-2 text-sm font-bold transition ${
                      page === i + 1
                        ? `${primaryBtnClass} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EarningLeaveFormReport;
